import type { FC } from 'react';

import {
  Heading,
  InputEmail,
  InputText,
  InputTelephone,
  Grid,
  InputNumber,
  RadioGroup,
  RadioButton,
} from '@eneco/sparky';
import { UseFormRegister, FieldErrors, Controller, Control } from 'react-hook-form';

import { hintOf } from './hint';
import type { FormFields } from './types';
import type { ContactFormFields } from '../contact-form-content.types';

type Props = {
  register: UseFormRegister<FormFields>;
  formFields: ContactFormFields;
  title: string | undefined;
  control: Control<FormFields, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  errors: FieldErrors<FormFields>;
};

const BudgetManagerContactInputs: FC<Props> = ({ register, control, formFields, title, errors }) => {
  const {
    firstNameFormField,
    surnamePrepositionFormField,
    surnameFormField,
    phoneNumberFormField,
    emailAddressFormField,
    businessNameFormField,
    postalCodeFormField,
    houseNumberFormField,
    houseNumberSuffixFormField,
    streetNameFormField,
    cityFormField,
    salutationFormField,
  } = formFields;
  return (
    <>
      <Heading size="S" as="h3">
        {title}
      </Heading>
      <InputText
        {...register('budgetManagerBusiness')}
        error={errors.budgetManagerBusiness?.message}
        label={businessNameFormField.label ?? ''}
        placeholder={businessNameFormField.placeholder ?? ''}
        hint={hintOf(businessNameFormField.hint)}
      />
      <Controller
        control={control}
        name="budgetManagerSalutation"
        render={({ field: { onChange, value, name } }) => (
          <RadioGroup
            name={name}
            label={salutationFormField.label ?? ''}
            hint={hintOf(salutationFormField.hint)}
            value={value}
            onValueChange={onChange}
            error={errors.budgetManagerSalutation?.message}>
            {salutationFormField?.options?.map(option => (
              <RadioButton value={option.name ?? ''} key={option.name}>
                {option.label ?? ''}
              </RadioButton>
            ))}
          </RadioGroup>
        )}
      />
      <Grid columns={{ initial: '1', md: '2', lg: '3' }} gap="6" alignY="end">
        <InputText
          {...register('budgetManagerFirstname')}
          label={firstNameFormField.label ?? ''}
          error={errors.budgetManagerFirstname?.message}
          placeholder={firstNameFormField.placeholder ?? ''}
          hint={hintOf(firstNameFormField.hint)}
        />
        <InputText
          {...register('budgetManagerSurnamePreposition')}
          label={surnamePrepositionFormField.label ?? ''}
          error={errors.budgetManagerSurnamePreposition?.message}
          isOptional
          placeholder={surnamePrepositionFormField.placeholder ?? ''}
          hint={hintOf(surnamePrepositionFormField.hint)}
        />
        <InputText
          {...register('budgetManagerSurname')}
          label={surnameFormField.label ?? ''}
          error={errors.budgetManagerSurname?.message}
          placeholder={surnameFormField.placeholder ?? ''}
          hint={hintOf(surnameFormField.hint)}
        />
      </Grid>
      <Grid columns={{ initial: '1', sm: '2' }} gap="6">
        <InputTelephone
          {...register('budgetManagerPhoneNumber')}
          label={phoneNumberFormField.label ?? ''}
          error={errors.budgetManagerPhoneNumber?.message}
          autoComplete="tel"
          placeholder={phoneNumberFormField.placeholder ?? ''}
          hint={hintOf(phoneNumberFormField.hint)}
        />
        <InputEmail
          {...register('budgetManagerEmailAddress')}
          label={emailAddressFormField.label ?? ''}
          error={errors.budgetManagerEmailAddress?.message}
          autoComplete="email"
          placeholder={emailAddressFormField.placeholder ?? ''}
          hint={hintOf(emailAddressFormField.hint)}
        />
      </Grid>
      <Grid columns={{ initial: '1', sm: '2', md: '3' }} gap="6">
        <InputText
          {...register('budgetManagerPostalCode')}
          label={postalCodeFormField.label ?? ''}
          error={errors.budgetManagerPostalCode?.message}
          placeholder={postalCodeFormField.placeholder ?? ''}
          hint={hintOf(postalCodeFormField.hint)}
        />
        <InputNumber
          {...register('budgetManagerHouseNumber')}
          label={houseNumberFormField.label ?? ''}
          error={errors.budgetManagerHouseNumber?.message}
          formatOptions={{ useGrouping: false }}
          placeholder={houseNumberFormField.placeholder ?? ''}
          hint={hintOf(houseNumberFormField.hint)}
        />
        <InputText
          {...register('budgetManagerHouseNumberSuffix')}
          label={houseNumberSuffixFormField.label ?? ''}
          error={errors.budgetManagerHouseNumberSuffix?.message}
          isOptional
          placeholder={houseNumberSuffixFormField.placeholder ?? ''}
          hint={hintOf(houseNumberSuffixFormField.hint)}
        />
      </Grid>
      <InputText
        {...register('budgetManagerStreetName')}
        label={streetNameFormField.label ?? ''}
        error={errors.budgetManagerStreetName?.message}
        placeholder={streetNameFormField.placeholder ?? ''}
        hint={hintOf(streetNameFormField.hint)}
      />
      <InputText
        {...register('budgetManagerCity')}
        label={cityFormField.label ?? ''}
        error={errors.budgetManagerCity?.message}
        placeholder={cityFormField.placeholder ?? ''}
        hint={hintOf(cityFormField.hint)}
      />
    </>
  );
};

export default BudgetManagerContactInputs;
